// Utils/focusUtils.js
//
// Central focus-navigation helpers for the Base form field library.
//
// STANDARD: every Base field (InputTextBox, SelectOption, DatePicker, CheckBox,
// RadioButton, Switch, CustomButton) accepts an `inputRef` prop and forwards it
// to its single actual DOM-focusable element (the <input>/<select>/<button>).
// That is the only ref parents ever need to hold, and it's what these helpers
// operate on to move focus to "whatever comes next".

export const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Returns focusable elements in DOM/tab order, skipping hidden/disabled ones.
export function getFocusableElements(container = document) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null // excludes display:none / detached elements
  );
}

// Moves focus to the next focusable element after `currentEl`.
// Returns true if focus moved, false if `currentEl` was last (or not found).
export function focusNext(currentEl, container = document) {
  if (!currentEl) return false;
  const focusable = getFocusableElements(container);
  const idx = focusable.indexOf(currentEl);
  if (idx > -1 && focusable[idx + 1]) {
    focusable[idx + 1].focus();
    return true;
  }
  return false;
}

// Moves focus to the previous focusable element before `currentEl`.
export function focusPrev(currentEl, container = document) {
  if (!currentEl) return false;
  const focusable = getFocusableElements(container);
  const idx = focusable.indexOf(currentEl);
  if (idx > 0) {
    focusable[idx - 1].focus();
    return true;
  }
  return false;
}

// Focuses the first focusable element in a container (e.g. a modal on open).
export function focusFirst(container = document) {
  const [first] = getFocusableElements(container);
  if (first) first.focus();
  return first || null;
}
